/**
 * @fileoverview Header Component - Main application header with navigation and tools
 * 
 * Provides the main application header with:
 * - Logo and branding
 * - Search functionality
 * - ETFBook and Media Centre quick access
 * - User settings and profile integration
 * - Navigation state management
 * - Responsive design elements
 * 
 * @component
 * @example
 * <Header />
 * 
 * @requires React
 * @requires react-router-dom
 * @requires ../Search/SearchBox
 * @requires ./HeaderSettings
 * @requires ./Logo
 * @requires ./Header.css
 * 
 * @author By John Bellew - Infinia.ie - Production Ready
 */

// Header.js
import React, { useState, useContext, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../Auth/AuthContext';
import SearchBox from '../Search/SearchBox';
import HeaderSettings from './HeaderSettings';
import Logo from './Logo';
import FeedbackModal from '../../modals/FeedbackModal';
import NotificationModal from '../../modals/NotificationModal';
import AlertModal from '../../modals/AlertModal';
import apiHelper from '../../utils/apiHelper';

import './Header.css';

/**
 * ETFBook Icon Component
 * 
 * Renders the ETFBook quick access icon in the header.
 * 
 * @component
 * @returns {JSX.Element} ETFBook navigation icon
 */
function SkillsIcon() {
  const navigate = useNavigate();
  const location = useLocation();

  const handleSkillsClick = () => {
    navigate('/Skills');
  };

  const isOnTalents = location.pathname.startsWith('/Skills');

  return (
    <div
      className={`skills-icon ${isOnTalents ? 'active' : ''}`}
      onClick={handleSkillsClick}
      title="Skills"
    >
      <img src="/images/talent-icon.png" alt="Skills" style={{ height: '38px', width: 'auto' }} />
    </div>
  );
}

function IntelligenceCentreIcon() {
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = location.pathname.startsWith('/InstitutionalIntelligence');

  return (
    <div
      className={`intelligence-centre-icon ${isActive ? 'active' : ''}`}
      onClick={() => navigate('/InstitutionalIntelligence')}
      title="Institutional Intelligence"
    >
      <img src="/images/intelligence-centre-icon-256.png" alt="Institutional Intelligence" style={{ height: '30px', width: 'auto' }} />
    </div>
  );
}

function ETFBookIcon() {
  const navigate = useNavigate();
  const location = useLocation();
  
  const handleETFBookClick = () => {
    navigate('/ETFBook');
  };

  const isOnETFBook = location.pathname.startsWith('/ETFBook');
  
  return (
    <div 
      className={`etfbook-icon ${isOnETFBook ? 'active' : ''}`}
      onClick={handleETFBookClick}
      title="ETF Screener - Professional ETF Analytics"
    >
      <img src="/assets/img/etfbook.ico" alt="ETF Screener" />
    </div>
  );
}

/**
 * Media Centre Icon Component
 * 
 * Renders the Media Centre quick access icon in the header.
 * 
 * @component
 * @returns {JSX.Element} Media Centre navigation icon
 */
function MediaCentreIcon() {
  const navigate = useNavigate();
  const location = useLocation();
  
  const handleMediaCentreClick = () => {
    navigate('/MediaCentre');
  };

  const isOnMediaCentre = location.pathname.startsWith('/MediaCentre');

  return (
    <div 
      className={`media-centre-icon ${isOnMediaCentre ? 'active' : ''}`}
      onClick={handleMediaCentreClick}
      title="Media Centre - Videos, Audio & Images"
    >
      <i className="fas fa-play-circle"></i>
    </div>
  );
}

/**
 * Feedback Icon Component
 * 
 * Renders the feedback quick access icon in the header.
 * 
 * @component
 * @param {Function} onFeedbackClick - Callback function to open feedback modal
 * @returns {JSX.Element} Feedback icon
 */
function FeedbackIcon({ onFeedbackClick }) {
  return (
    <div 
      className="feedback-icon"
      onClick={onFeedbackClick}
      title="Share Your Feedback - Help us improve!"
    >
      <i className="fas fa-comment-dots"></i>
    </div>
  );
}

/**
 * Notification Icon Component
 * 
 * Renders the notification icon in the header with live notification count.
 * 
 * @component
 * @param {Function} onNotificationClick - Callback to open notification modal
 * @param {number} notificationCount - Current unread notification count
 * @param {Function} setNotificationCount - Function to update notification count
 * @param {string} userEmail - User's email address for API calls
 * @param {Object} user - Full user object from AuthContext
 * @param {boolean} authLoading - Loading state from AuthContext
 * @returns {JSX.Element} Notification icon
 */
const NotificationIcon = forwardRef(({ onNotificationClick, notificationCount, setNotificationCount, userEmail, user, authLoading }, ref) => {
  const [isLoading, setIsLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const intervalRef = useRef(null);
  const isVisibleRef = useRef(true);

  // Track page visibility to pause polling when tab is not visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      isVisibleRef.current = !document.hidden;
      
      if (isVisibleRef.current && userEmail && user) {
        // Page became visible, refresh count immediately
        fetchNotificationCount(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [userEmail, user]);

  // Fetch notification count function with retry logic
  const fetchNotificationCount = async (isRetry = false) => {
    try {
      if (!isRetry) {
        setIsLoading(true);
      }
      
      if (!userEmail) {
        console.warn('No user email available for notification count');
        setNotificationCount(0);
        setIsLoading(false);
        return;
      }
      
      const countData = await apiHelper.getNotificationCount(userEmail);
      setNotificationCount(countData.unreadCount || 0);
      setRetryCount(0); // Reset retry count on success
    } catch (error) {
      console.warn('Failed to fetch notification count:', error.message);
      
      // Only retry for non-auth errors and if we haven't exceeded retry limit
      if (retryCount < 2 && userEmail && !error.message.includes('auth')) {
        setRetryCount(prev => prev + 1);
        setTimeout(() => fetchNotificationCount(true), 3000 * (retryCount + 1)); // Exponential backoff
      } else {
        // For auth errors or max retries, just set to 0 and stop
        setNotificationCount(0);
        setRetryCount(0);
      }
    } finally {
      if (!isRetry) {
        setIsLoading(false);
      }
    }
  };

  // Expose refreshCount method to parent via ref
  useImperativeHandle(ref, () => ({
    refreshCount: fetchNotificationCount
  }));

  // Clear existing interval
  const clearNotificationInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // Setup notification count fetching and interval
  useEffect(() => {
    // Clear any existing interval first
    clearNotificationInterval();

    // Don't fetch if auth is still loading
    if (authLoading) {
      setIsLoading(true);
      return;
    }

    // Reset retry count when user changes
    setRetryCount(0);

    // Only fetch if we have a valid user with email
    if (user && userEmail) {
      fetchNotificationCount();

      // Set up refresh interval (every 5 minutes, but only when page is visible)
      intervalRef.current = setInterval(() => {
        // Only poll if page is visible to reduce unnecessary API calls
        if (isVisibleRef.current) {
          fetchNotificationCount(true); // Silent retry for interval updates
        }
      }, 5 * 60 * 1000);
    } else {
      // User not authenticated, reset count
      setNotificationCount(0);
      setIsLoading(false);
    }

    // Cleanup interval on unmount or dependency change
    return clearNotificationInterval;
  }, [userEmail, user, authLoading]); // Include all relevant dependencies

  // Additional effect to handle auth state changes specifically
  useEffect(() => {
    // When auth finishes loading and we have a user, ensure we fetch notifications
    if (!authLoading && user && userEmail && notificationCount === 0 && !isLoading) {
      fetchNotificationCount();
    }
  }, [authLoading, user, userEmail]); // Trigger when auth completes

  return (
    <div 
      className="notification-icon"
      onClick={onNotificationClick}
      title={`${notificationCount} unread notifications`}
    >
      <i className="fas fa-bell"></i>
      {!isLoading && notificationCount > 0 && (
        <span className="notification-badge">
          {notificationCount > 99 ? '99+' : notificationCount}
        </span>
      )}
    </div>
  );
});

/**
 * Header Component
 * 
 * Renders the main application header with logo, search, quick access icons, and user settings.
 * 
 * Features:
 * - Logo with home navigation
 * - Search functionality integration
 * - ETFBook quick access
 * - Media Centre quick access  
 * - Feedback modal integration
 * - User settings and profile
 * - Responsive design
 * 
 * @component
 * @returns {JSX.Element} Main application header
 */
function Header() {
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
  const [alertNotifications, setAlertNotifications] = useState([]);
  const notificationIconRef = useRef(null);
  
  const {
    user: [user],
    loading: [authLoading],
  } = useContext(AuthContext);

  const handleFeedbackClick = () => {
    setIsFeedbackModalOpen(true);
  };

  const handleFeedbackClose = () => {
    setIsFeedbackModalOpen(false);
  };

  const handleNotificationClick = () => {
    setIsNotificationModalOpen(true);
  };

  const handleNotificationClose = () => {
    setIsNotificationModalOpen(false);
  };

  const handleNotificationCountChange = (newCount) => {
    setNotificationCount(newCount);
  };

  /**
   * Check for alert notifications and show them automatically
   */
  const checkForAlerts = async () => {
    if (!userEmail || authLoading) return;

    try {
      const response = await apiHelper.getNotifications(userEmail);
      
      // Handle the new response format that doesn't throw on errors
      if (response?.success && response?.notifications) {
        // Filter out Alert notifications that need to be shown immediately
        const alerts = response.notifications.filter(notification => 
          notification.type === 'Alert' && !notification.isRead
        );
        
        if (alerts.length > 0) {
          setAlertNotifications(alerts);
          setIsAlertModalOpen(true);
        }
      } else if (response?.success === false) {
        // Auth not ready yet, don't log as error
        console.log('Alerts check skipped - auth not ready');
      }
    } catch (error) {
      console.warn('Error checking for alerts:', error.message);
    }
  };

  /**
   * Handle alert dismissal
   */
  const handleAlertDismiss = () => {
    setIsAlertModalOpen(false);
    setAlertNotifications([]);
    // Refresh notification count after alerts are dismissed
    if (notificationIconRef.current?.refreshCount) {
      notificationIconRef.current.refreshCount();
    }
  };

  // Get user email from AuthContext (same pattern as HeaderSettings)
  const userEmail = user?.profile?.email;

  // Check for alerts when authentication completes and user is available
  useEffect(() => {
    if (!authLoading && user && userEmail) {
      // Add a small delay after login to let Auth0 fully initialize
      const timer = setTimeout(() => {
        checkForAlerts();
      }, 2000); // 2 second delay after auth completes

      return () => clearTimeout(timer);
    }
  }, [userEmail, user, authLoading]);
 
  return (
    <>
    <header className="wrapper header-container top-header container">
            <NavLink to="/"><Logo /></NavLink>
            <SearchBox />
            <IntelligenceCentreIcon />
            <SkillsIcon />
            <ETFBookIcon />
            <MediaCentreIcon />
              <NotificationIcon 
                ref={notificationIconRef}
                onNotificationClick={handleNotificationClick}
                notificationCount={notificationCount}
                setNotificationCount={setNotificationCount}
                userEmail={userEmail}
                user={user}
                authLoading={authLoading}
              />
              <FeedbackIcon onFeedbackClick={handleFeedbackClick} />
            <HeaderSettings />
    </header>
      
      <FeedbackModal 
        isOpen={isFeedbackModalOpen} 
        onClose={handleFeedbackClose}
        userEmail={userEmail}
      />

      <NotificationModal 
        isOpen={isNotificationModalOpen} 
        onClose={handleNotificationClose}
        onNotificationCountChange={handleNotificationCountChange}
        userEmail={userEmail}
        excludeAlerts={true}
      />

      {/* Alert Modal - Shows critical alerts automatically */}
      <AlertModal
        isOpen={isAlertModalOpen}
        alerts={alertNotifications}
        onClose={() => setIsAlertModalOpen(false)}
        onDismiss={handleAlertDismiss}
        userEmail={userEmail}
      />
    </>
  );
}

export default Header;
